import type { RebalScheduler, ReconScheduler } from '../types';
import { FREQUENCY_ORDER } from '../config/constants';

/**
 * Normalize a scheduler block to the backend's "disabled field = null" contract.
 *
 * The form greys out fields that don't apply to the current frequency / timing /
 * target, but the stored values can linger. The emitted `scheduler` dict (and the
 * Match-to-Universe preset) must reflect the disabled state explicitly as `null`
 * rather than a stale value — mirroring quant_toolbox scheduler_type.py validation:
 *
 *   - Daily ('D')   → timing, first_month, week_of_month, weekday all null.
 *   - Weekly ('W')  → timing, first_month, week_of_month null; weekday kept.
 *   - first_month   → only for Q/SA/A (the anchored periodic frequencies).
 *   - week_of_month / weekday → only for a CUSTOM_INTRA_MONTH timing.
 *   - custom_lag_days → only when ref_date is CUSTOM_LAG.
 *   - rebalance recompute carries a ref_date; use_existing nulls it.
 */
function isShortFreq(frequency: ReconScheduler['frequency'] | null): boolean {
  return frequency === 'D' || frequency === 'W';
}

function isAnchoredFreq(
  frequency: ReconScheduler['frequency'] | null,
): boolean {
  return frequency === 'Q' || frequency === 'SA' || frequency === 'A';
}

export function normalizeReconScheduler(r: ReconScheduler): ReconScheduler {
  const short = isShortFreq(r.frequency);
  const intra = !short && r.timing === 'CUSTOM_INTRA_MONTH';
  return {
    frequency: r.frequency,
    timing: short ? null : r.timing,
    first_month: isAnchoredFreq(r.frequency) ? r.first_month : null,
    week_of_month: intra ? r.week_of_month : null,
    weekday:
      r.frequency === 'D' ? null : intra || r.frequency === 'W' ? r.weekday : null,
    ref_date: r.ref_date,
    custom_lag_days: r.ref_date === 'CUSTOM_LAG' ? r.custom_lag_days : null,
  };
}

export function normalizeRebalScheduler(r: RebalScheduler): RebalScheduler {
  // frequency can be null in exactly one state (the pre-2020 dead end); the
  // config is invalid then and never reaches the backend as-is, but the
  // normalizer must not crash on it.
  const short = r.frequency != null && isShortFreq(r.frequency);
  const intra = !short && r.timing === 'CUSTOM_INTRA_MONTH';
  const recompute = r.rebal_target === 'recompute';
  return {
    rebal_methodology: r.rebal_methodology,
    frequency: r.frequency,
    timing: short ? null : r.timing,
    first_month: isAnchoredFreq(r.frequency) ? r.first_month : null,
    week_of_month: intra ? r.week_of_month : null,
    weekday:
      r.frequency === 'D' ? null : intra || r.frequency === 'W' ? r.weekday : null,
    ref_date: recompute ? r.ref_date : null,
    custom_lag_days:
      recompute && r.ref_date === 'CUSTOM_LAG' ? r.custom_lag_days : null,
    rebal_target: r.rebal_target,
  };
}

/** Backtests starting before this date can't run Daily/Weekly schedules. */
export const HIGH_FREQ_CUTOFF = '2020-01-01';

export function isBeforeHighFreqCutoff(
  startDate: string | null | undefined,
): boolean {
  return startDate ? new Date(startDate) < new Date(HIGH_FREQ_CUTOFF) : false;
}

/**
 * THE FILL CONTRACT: the store always holds a concrete value for every
 * schedule field — inapplicable ones are nulled at EMIT ONLY (the normalize
 * functions below). These are the canonical defaults every transition
 * backfills a newly-applicable null with, so an enabled select can never
 * render empty. The single sanctioned empty is the rebalance FREQUENCY in
 * the pre-2020 + Monthly-recon dead end (no strictly-finer legal frequency).
 */
export function backfillReconDefaults(r: ReconScheduler): ReconScheduler {
  const short = isShortFreq(r.frequency);
  return {
    ...r,
    timing: !short && r.timing == null ? 'CUSTOM_INTRA_MONTH' : r.timing,
    first_month:
      isAnchoredFreq(r.frequency) && r.first_month == null ? 6 : r.first_month,
    week_of_month: r.week_of_month ?? 3,
    weekday: r.weekday ?? 'FRI',
    custom_lag_days:
      r.ref_date === 'CUSTOM_LAG' && r.custom_lag_days == null
        ? 10
        : r.custom_lag_days,
  };
}

export function backfillRebalDefaults(r: RebalScheduler): RebalScheduler {
  const short = r.frequency != null && isShortFreq(r.frequency);
  return {
    ...r,
    timing:
      r.frequency != null && !short && r.timing == null
        ? 'CUSTOM_INTRA_MONTH'
        : r.timing,
    first_month:
      r.frequency != null &&
      isAnchoredFreq(r.frequency) &&
      r.first_month == null
        ? 3
        : r.first_month,
    week_of_month: r.week_of_month ?? 3,
    weekday: r.weekday ?? 'FRI',
    // Unconditional: even under use_existing (where the emit nulls it) the
    // greyed select shows "Previous Month End" instead of a blank.
    ref_date: r.ref_date ?? 'PREV_MONTH_END',
    custom_lag_days:
      r.ref_date === 'CUSTOM_LAG' && r.custom_lag_days == null
        ? 10
        : r.custom_lag_days,
  };
}

/**
 * The pre-2020 auto-downgrade, as a pure function: Daily/Weekly aren't
 * available for backtests starting before HIGH_FREQ_CUTOFF, so the schedule
 * forms coerce recon to Monthly + month-end reference and rebalance to
 * Monthly (useRebalancingSchedule's effect applies exactly these patches).
 * Both downgraded blocks run through the backfills, so the promoted fields
 * (a Monthly's timing, its week/weekday) land WITH values — a Daily preset
 * used to downgrade to Monthly with a null timing, leaving the Rebalance
 * Timing select enabled-but-empty.
 *
 * "Synced to Universe" MUST compare against the preset run through this same
 * coercion — the US indices' preset carries a Daily rebalance, so over a
 * pre-2020 start (any MAX window) applying the preset un-synced itself the
 * instant the downgrade effect fired, and the badge could never appear.
 */
/** True for the two day-based references banned before HIGH_FREQ_CUTOFF. */
function isDayBasedRef(ref: string | null): boolean {
  return ref === 'PREV_BUSINESS_DAY' || ref === 'CUSTOM_LAG';
}

/**
 * The complete pre-2020 coercion, unconditionally applied — covers EVERY
 * selection the window bans, so a start date crossing from post- to pre-2020
 * falls back cleanly no matter what was picked while it was legal:
 *   • D/W frequencies → Monthly (recon also snaps to month-end reference);
 *   • day-based references (T-1 / Custom Lag) → Previous Month End, on BOTH
 *     schedules;
 *   • a rebalance no longer strictly finer than reconstitution (the pre-2020
 *     Monthly dead end) → frequency null, the single sanctioned empty;
 *   • both blocks re-run the backfills so promoted fields land with values.
 */
export function downgradeForPre2020<
  T extends {
    recon_scheduler: ReconScheduler;
    is_rebal_activated: boolean;
    rebal_scheduler: RebalScheduler;
  },
>(scheduler: T): T {
  let recon = scheduler.recon_scheduler;
  if (isShortFreq(recon.frequency)) {
    recon = { ...recon, frequency: 'M', timing: 'CUSTOM_INTRA_MONTH' };
  }
  if (isDayBasedRef(recon.ref_date)) {
    recon = { ...recon, ref_date: 'PREV_MONTH_END', custom_lag_days: null };
  }
  recon = backfillReconDefaults(recon);

  let rebal = scheduler.rebal_scheduler;
  if (rebal.frequency != null && isShortFreq(rebal.frequency)) {
    rebal = { ...rebal, frequency: 'M' };
  }
  if (
    rebal.frequency != null &&
    FREQUENCY_ORDER[rebal.frequency] >= FREQUENCY_ORDER[recon.frequency]
  ) {
    rebal = { ...rebal, frequency: null };
  }
  if (isDayBasedRef(rebal.ref_date)) {
    rebal = { ...rebal, ref_date: 'PREV_MONTH_END', custom_lag_days: null };
  }
  rebal = backfillRebalDefaults(rebal);

  return { ...scheduler, recon_scheduler: recon, rebal_scheduler: rebal };
}

export function applyWindowDowngrade<
  T extends {
    recon_scheduler: ReconScheduler;
    is_rebal_activated: boolean;
    rebal_scheduler: RebalScheduler;
  },
>(scheduler: T, startDate: string | null | undefined): T {
  return isBeforeHighFreqCutoff(startDate)
    ? downgradeForPre2020(scheduler)
    : scheduler;
}

/**
 * Normalize the full RebalanceSchedulerConfig block emitted to the backend.
 *
 * Crucially, ``rebal_scheduler`` is emitted as ``null`` whenever
 * ``is_rebal_activated`` is false — matching the backend's
 * ``_validate_rebal_activation`` contract (off ⇒ null, on ⇒ present) at the
 * source, so the persisted config carries no ignored "shell" when rebalancing
 * is disabled (the backend would otherwise null it via a before-validator).
 */
export function normalizeSchedulerConfig(s: {
  recon_scheduler: ReconScheduler;
  is_rebal_activated: boolean;
  rebal_scheduler: RebalScheduler;
}): {
  recon_scheduler: ReconScheduler;
  is_rebal_activated: boolean;
  rebal_scheduler: RebalScheduler | null;
} {
  return {
    recon_scheduler: normalizeReconScheduler(s.recon_scheduler),
    is_rebal_activated: s.is_rebal_activated,
    rebal_scheduler: s.is_rebal_activated
      ? normalizeRebalScheduler(s.rebal_scheduler)
      : null,
  };
}

/**
 * Preview-only scheduler config that degrades gracefully so the timeline +
 * snapshot-stats endpoints never 422.
 *
 * Both /scheduler/dates and /scheduler/snapshot-stats validate a
 * RebalanceSchedulerConfig, so an INVALID rebalance (a frequency that isn't
 * strictly more frequent than reconstitution — which can't produce valid
 * inter-reconstitution dates) fails the whole call and takes the always-valid
 * reconstitution timeline down with it. For the PREVIEW we drop an invalid
 * rebalance (recon-only) so the reconstitution timeline always renders.
 * buildStrategyConfig keeps the value for the real backtest, where the
 * Rebalance Frequency Rule warning still blocks the run.
 */
export function previewSchedulerConfig(s: {
  recon_scheduler: ReconScheduler;
  is_rebal_activated: boolean;
  rebal_scheduler: RebalScheduler;
}): ReturnType<typeof normalizeSchedulerConfig> {
  const rebalValid =
    s.is_rebal_activated &&
    !!s.rebal_scheduler.frequency &&
    FREQUENCY_ORDER[s.rebal_scheduler.frequency] <
      FREQUENCY_ORDER[s.recon_scheduler.frequency];
  return normalizeSchedulerConfig({ ...s, is_rebal_activated: rebalValid });
}
